/**
 * Google Cloud TTS does not honour newlines as sentence boundaries for every
 * language. Latin-script voices pause on `\n`, but CJK (Chinese/Japanese/Korean)
 * voices treat a whole multi-line block as one run-on "sentence" until the next
 * ideographic full stop `。`, producing pause-less audio for bulleted lists and
 * short paragraphs.
 *
 * There is no API parameter to control this (`AudioConfig` has no segmentation
 * field) and the affected languages use Chirp 3 HD voices, which reject SSML
 * entirely — so we cannot use `<s>`/`<p>` tags either. The reliable, voice-model
 * agnostic fix is to give the engine the punctuation it segments on: append a
 * script-appropriate sentence terminator to each line that lacks one. Punctuation
 * is not spoken, it only shapes prosody, so this is safe.
 *
 * Scoped to CJK Google voices only, and applied before the JiT cache key is
 * hashed so keys change naturally and old run-on cache entries are orphaned.
 */

// Last meaningful char already implies a stop/pause — don't append (avoids `,。`).
// Latin: . ! ? ; :  — CJK/fullwidth: 。 ！ ？ ； ： … 、 ， plus ASCII comma.
const ALREADY_TERMINATED = /[.!?;:。！？；：…、，,]$/;

// Any CJK codepoint on the line ⇒ use the ideographic full stop.
// Kana, CJK ideographs (+ ext A), compatibility ideographs, Hangul, CJK punctuation.
const CJK_CHAR = /[぀-ヿ㐀-䶿一-鿿豈-﫿가-힯　-〿]/;

// BCP-47 voice locales that don't pause on newlines: Mandarin (cmn-*), Cantonese
// (yue-*), generic Chinese (zh-*), Japanese (ja-*), Korean (ko-*).
const CJK_LOCALE = /^(cmn|yue|zh|ja|ko)(-|$)/i;

const terminatorForLine = (line: string): string =>
  CJK_CHAR.test(line) ? '。' : '.';

/**
 * Append a script-appropriate sentence terminator to each line of `text` that
 * does not already end in terminal/pausing punctuation.
 *
 * No-op unless the provider is Google and `voiceLocale` is a CJK locale. Also a
 * no-op for single-line text (a lone sentence needs no help, even with a trailing
 * newline) — the run-on problem only occurs across multiple lines. Blank lines
 * are preserved untouched.
 */
export default function normalizeGoogleNarration(
  text: string,
  provider: string,
  voiceLocale: string
): string {
  if (provider !== 'google' || !text || !CJK_LOCALE.test(voiceLocale)) {
    return text;
  }

  const lines = text.split('\n');
  const contentLineCount = lines.filter(line => line.trim()).length;
  if (contentLineCount <= 1) {
    return text;
  }

  return lines
    .map(line => {
      const trimmed = line.replace(/[ \t]+$/, '');
      if (!trimmed) {
        return line;
      }
      if (ALREADY_TERMINATED.test(trimmed)) {
        return trimmed;
      }
      return trimmed + terminatorForLine(trimmed);
    })
    .join('\n');
}
